/**
 * Brazilian Document Parser
 *
 * Parses OCR text to extract structured data from Brazilian documents
 * (RG, CNH, CPF cards).
 */

export interface ParsedDocument {
  firstName?: string;
  lastName?: string;
  cpf?: string;
  rg?: string;
  birthDate?: string;
  documentType?: 'rg' | 'cnh' | 'cpf_card' | 'unknown';
  confidence: number;
  rawText: string;
}

// CPF pattern: XXX.XXX.XXX-XX or XXXXXXXXXXX
const CPF_PATTERN = /\b(\d{3}[.\s]?\d{3}[.\s]?\d{3}[-.\s]?\d{2})\b/g;

// RG patterns vary by state, but common formats:
// XX.XXX.XXX-X, XXXXXXXXX, XX XXX XXX X
const RG_PATTERN = /\b(\d{1,2}[.\s]?\d{3}[.\s]?\d{3}[-.\s]?\d{1})\b|\bRG[:\s]*(\d{7,9})\b/gi;

// Date patterns: DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY
const DATE_PATTERN = /\b(\d{2}[\/\-\.]\d{2}[\/\-\.]\d{4})\b/g;

// Common name prefixes/labels in documents
const NAME_LABELS = [
  'NOME',
  'NOME COMPLETO',
  'NOME DO TITULAR',
  'TITULAR',
  'NOME CIVIL',
];

// Common Brazilian first names (for validation)
const COMMON_FIRST_NAMES = new Set([
  'JOSE', 'JOAO', 'ANTONIO', 'FRANCISCO', 'CARLOS', 'PAULO', 'PEDRO', 'LUCAS',
  'LUIZ', 'MARCOS', 'LUIS', 'GABRIEL', 'RAFAEL', 'DANIEL', 'MARCELO', 'BRUNO',
  'EDUARDO', 'FELIPE', 'RODRIGO', 'SERGIO', 'FERNANDO', 'GUSTAVO', 'ANDRE',
  'MARIA', 'ANA', 'FRANCISCA', 'ANTONIA', 'ADRIANA', 'JULIANA', 'MARCIA',
  'FERNANDA', 'PATRICIA', 'ALINE', 'SANDRA', 'CAMILA', 'AMANDA', 'BRUNA',
  'JESSICA', 'LETICIA', 'JULIA', 'LUCIANA', 'VANESSA', 'MARIANA', 'GABRIELA',
]);

/**
 * Clean and normalize CPF to format XXX.XXX.XXX-XX
 */
export function normalizeCPF(cpf: string): string {
  // Remove all non-digits
  const digits = cpf.replace(/\D/g, '');

  if (digits.length !== 11) return '';

  // Format as XXX.XXX.XXX-XX
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Validate CPF using modulo 11 algorithm
 */
export function validateCPF(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, '');

  if (digits.length !== 11) return false;

  // Check for known invalid CPFs (all same digits)
  if (/^(\d)\1{10}$/.test(digits)) return false;

  // Validate first check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i]) * (10 - i);
  }
  let checkDigit = (sum * 10) % 11;
  if (checkDigit === 10) checkDigit = 0;
  if (checkDigit !== parseInt(digits[9])) return false;

  // Validate second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i]) * (11 - i);
  }
  checkDigit = (sum * 10) % 11;
  if (checkDigit === 10) checkDigit = 0;
  if (checkDigit !== parseInt(digits[10])) return false;

  return true;
}

/**
 * Normalize RG (remove special characters, keep only digits and X)
 */
export function normalizeRG(rg: string): string {
  return rg.replace(/[^\dXx]/g, '').toUpperCase();
}

/**
 * Normalize date to DD/MM/YYYY format
 */
export function normalizeDate(date: string): string {
  // Replace separators with /
  return date.replace(/[-\.]/g, '/');
}

/**
 * Validate date is a reasonable birth date
 */
export function isValidBirthDate(dateStr: string): boolean {
  const normalized = normalizeDate(dateStr);
  const parts = normalized.split('/');

  if (parts.length !== 3) return false;

  const day = parseInt(parts[0]);
  const month = parseInt(parts[1]);
  const year = parseInt(parts[2]);

  if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
  if (day < 1 || day > 31) return false;
  if (month < 1 || month > 12) return false;

  // Birth year should be between 1900 and current year - 1
  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear - 1) return false;

  return true;
}

/**
 * Extract CPF from text
 */
export function extractCPF(text: string): string | undefined {
  const matches = text.match(CPF_PATTERN);
  if (!matches) return undefined;

  // Try to find a valid CPF
  for (const match of matches) {
    const normalized = normalizeCPF(match);
    if (normalized && validateCPF(normalized)) {
      return normalized;
    }
  }

  // If no valid CPF found, return the first match anyway (user can verify)
  const firstMatch = matches[0];
  const normalized = normalizeCPF(firstMatch);
  return normalized || undefined;
}

/**
 * Extract RG from text
 */
export function extractRG(text: string): string | undefined {
  const matches = text.match(RG_PATTERN);
  if (!matches) return undefined;

  // Return the first match, normalized
  for (const match of matches) {
    const normalized = normalizeRG(match);
    if (normalized.length >= 7) {
      return normalized;
    }
  }

  return undefined;
}

/**
 * Extract birth date from text
 */
export function extractBirthDate(text: string): string | undefined {
  const matches = text.match(DATE_PATTERN);
  if (!matches) return undefined;

  // Find a date that looks like a birth date (not too recent, not too old)
  for (const match of matches) {
    if (isValidBirthDate(match)) {
      return normalizeDate(match);
    }
  }

  return undefined;
}

/**
 * Extract name from text using various heuristics
 */
export function extractName(text: string): { firstName?: string; lastName?: string } {
  const lines = text.toUpperCase().split('\n').map(line => line.trim()).filter(Boolean);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check if this line is a name label
    const isLabel = NAME_LABELS.some(label => line.includes(label));

    if (isLabel) {
      // Check next line for the actual name
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1];
        const nameParts = extractNameFromLine(nextLine);
        if (nameParts.firstName) {
          return nameParts;
        }
      }

      // Check same line after the label
      for (const label of NAME_LABELS) {
        if (line.includes(label)) {
          const afterLabel = line.split(label)[1]?.trim();
          if (afterLabel && afterLabel.length > 3) {
            const cleanName = afterLabel.replace(/[^A-Z\s]/g, '').trim();
            const nameParts = extractNameFromLine(cleanName);
            if (nameParts.firstName) {
              return nameParts;
            }
          }
        }
      }
    }
  }

  // Fallback: look for lines that look like names (multiple capitalized words)
  for (const line of lines) {
    const cleanLine = line.replace(/[^A-Z\s]/g, '').trim();
    const words = cleanLine.split(/\s+/).filter(w => w.length >= 2);

    if (words.length >= 2 && words.length <= 6) {
      // Check if first word is a common name
      if (COMMON_FIRST_NAMES.has(words[0])) {
        return {
          firstName: words[0],
          lastName: words.slice(1).join(' '),
        };
      }
    }
  }

  return {};
}

/**
 * Extract first and last name from a line of text
 */
function extractNameFromLine(line: string): { firstName?: string; lastName?: string } {
  const cleanLine = line.replace(/[^A-Z\s]/g, '').trim();
  const words = cleanLine.split(/\s+/).filter(w => w.length >= 2);

  if (words.length >= 2) {
    return {
      firstName: words[0],
      lastName: words.slice(1).join(' '),
    };
  }

  if (words.length === 1 && words[0].length >= 2) {
    return { firstName: words[0] };
  }

  return {};
}

/**
 * Detect document type based on text content
 */
export function detectDocumentType(text: string): 'rg' | 'cnh' | 'cpf_card' | 'unknown' {
  const upperText = text.toUpperCase();

  // CNH indicators
  if (
    upperText.includes('CARTEIRA NACIONAL DE HABILITACAO') ||
    upperText.includes('CNH') ||
    upperText.includes('HABILITACAO') ||
    upperText.includes('CATEGORIA') ||
    upperText.includes('RENACH')
  ) {
    return 'cnh';
  }

  // CPF card indicators
  if (
    upperText.includes('CADASTRO DE PESSOA') ||
    upperText.includes('CPF') && upperText.includes('RECEITA FEDERAL')
  ) {
    return 'cpf_card';
  }

  // RG indicators
  if (
    upperText.includes('REGISTRO GERAL') ||
    upperText.includes('IDENTIDADE') ||
    upperText.includes('CEDULA DE IDENTIDADE') ||
    upperText.includes('SSP') ||
    upperText.includes('SECRETARIA DE SEGURANCA')
  ) {
    return 'rg';
  }

  return 'unknown';
}

/**
 * Calculate confidence score based on how much data was extracted
 */
function calculateConfidence(result: Partial<ParsedDocument>): number {
  let score = 0;
  let maxScore = 0;

  // CPF with validation is worth more
  if (result.cpf) {
    maxScore += 30;
    if (validateCPF(result.cpf)) {
      score += 30;
    } else {
      score += 15; // Partial credit for finding a CPF-like number
    }
  }

  // Name
  if (result.firstName) {
    maxScore += 25;
    score += 25;
    if (result.lastName) {
      maxScore += 15;
      score += 15;
    }
  }

  // RG
  if (result.rg) {
    maxScore += 15;
    score += 15;
  }

  // Birth date
  if (result.birthDate) {
    maxScore += 15;
    if (isValidBirthDate(result.birthDate)) {
      score += 15;
    } else {
      score += 7;
    }
  }

  // Document type identified
  if (result.documentType && result.documentType !== 'unknown') {
    score += 10;
  }

  // Return percentage, minimum 10% if we found anything
  if (maxScore === 0) return 0;
  return Math.max(10, Math.round((score / 100) * 100));
}

/**
 * Parse OCR text and extract structured document data
 */
export function parseDocument(rawText: string, ocrConfidence: number = 100): ParsedDocument {
  const documentType = detectDocumentType(rawText);
  const cpf = extractCPF(rawText);
  const rg = extractRG(rawText);
  const birthDate = extractBirthDate(rawText);
  const { firstName, lastName } = extractName(rawText);

  const result: ParsedDocument = {
    firstName,
    lastName,
    cpf,
    rg,
    birthDate,
    documentType,
    rawText,
    confidence: 0,
  };

  // Calculate confidence based on extraction quality
  const extractionConfidence = calculateConfidence(result);

  // Combine OCR confidence with extraction confidence
  result.confidence = Math.round((ocrConfidence + extractionConfidence) / 2);

  return result;
}
